import Fuse from 'fuse.js';
import { SortDirection } from '@robocorp/components';

export type SearchRules<T> = {
  readonly [key: string]: {
    value: (item: T) => string | number | undefined | null;
  };
};

/**
 * Local data fuzzy search helper util
 *
 * @example
 * const searchedData = fuzzySearchData({ foo: { value: (item) => item.foo } }, [{ foo: 'bar' }, { foo: 'baz' }])('ba');
 */
export const fuzzySearchData = <T>(searchRules: SearchRules<T>, data: T[]) => {
  const fuseKeys = Object.entries(searchRules).map(([name, { value }]) => ({
    name,
    getFn: (item: T): string => {
      const itemValue = value(item);
      if (typeof itemValue === 'number') {
        return String(itemValue);
      }
      return itemValue ?? '';
    },
  }));

  const fuse = new Fuse(data, {
    threshold: 0.4,
    shouldSort: true,
    keys: fuseKeys,
  });

  return (search: string) => {
    if (search === '') {
      return data;
    }

    return fuse.search(search).map(({ item }) => item);
  };
};

/**
 * Local data search helper util
 *
 * @example
 * const searchedData = searchData({ foo: { value: (item) => item.foo } }, [{ foo: 'bar' }, { foo: 'baz' }], 'ba');
 */
export const searchData = <T>(searchRules: SearchRules<T>, data: T[], search: string) => {
  if (search === '') {
    return data;
  }

  return data.filter((item) =>
    Object.values(searchRules).some((rule) => {
      const value = rule.value(item);
      return typeof value === 'string' || typeof value === 'number'
        ? String(value).toLowerCase().includes(search.toLowerCase())
        : false;
    }),
  );
};

export type SortRules<T> = {
  readonly [key: string]: {
    type: 'string' | 'number' | 'date';
    value: (item: T) => string | number | undefined | null;
  };
};

/**
 * Local data sorting helper util
 *
 * @example
 * const sortedData = sortData(
 *   { foo: { type: 'string', value: (item) => item.foo } },
 *   [{ foo: 'bar' }, { foo: 'baz' }],
 *   ['foo', 'asc'],
 * );
 */
export const sortData = <T>(
  sortRules: SortRules<T>,
  data: T[],
  sort: [string, SortDirection] | null,
) => {
  if (!sort) {
    return data;
  }

  const sortRule = sortRules[sort[0]];
  const sortOrder = sort[1] === 'desc' ? 1 : -1;

  if (!sortRule) {
    return data;
  }

  const result = [...data];

  result.sort((a, b) => {
    const valueA = sortRule.value(a);
    const valueB = sortRule.value(b);

    if (valueA == null && valueB == null) {
      return 0;
    }

    if (valueA == null) {
      return 1 * sortOrder;
    }

    if (valueB == null) {
      return -1 * sortOrder;
    }

    if (sortRule.type === 'string') {
      return (valueA as string).toLowerCase().localeCompare(valueB as string) * sortOrder;
    }

    if (sortRule.type === 'number') {
      return ((valueB as number) - (valueA as number)) * sortOrder;
    }

    if (sortRule.type === 'date') {
      return (new Date(valueB).getTime() - new Date(valueA).getTime()) * sortOrder;
    }
    return 0;
  });

  return result;
};

export type FilterRules<T> = {
  readonly [key: string]: {
    value: (item: T) => string | number | undefined | null;
  };
};

/**
 * Local data filtering helper util
 *
 * @example
 * const filteredData = filterData({ fooValues: { value: (item) => item.foo } }, [{ foo: 'bar' }, { foo: 'baz' }], {
 *   fooValues: ['baz'],
 * });
 */
export const filterData = <T>(
  filterRules: FilterRules<T>,
  data: T[],
  filters: Record<string, unknown[]>,
) => {
  if (!filters) {
    return data;
  }

  return data.filter((item) =>
    Object.entries(filters).every(([filterKey, filterValues]) => {
      const value = filterRules[filterKey]?.value(item);
      return filterValues.length === 0 || (value !== undefined && filterValues.includes(value));
    }),
  );
};
